/**
 * Reseller "Buy Credits" page.
 *
 * Shows the reseller's current balance + the credit packs the admin
 * has published. Clicking Buy on a pack:
 *  1. Creates a purchase-intent record on the server (returns order_id).
 *  2. Opens the same gateway picker used on the public /activation page.
 *  3. Redirects the reseller to PayPal / PayFast / Stripe with the
 *     `order_id` embedded in the callback URL so the admin can
 *     reconcile the payment and top the reseller up.
 */
import React, { useEffect, useState } from "react";
import {
  getResellerCreditPacks,
  createResellerPurchaseIntent,
} from "../lib/api";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  Wallet,
  Package,
  CreditCard,
  RefreshCw,
  CheckCircle2,
  Loader2,
  Infinity as InfinityIcon,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function BuyCredits() {
  const { toast } = useToast();
  const [state, setState] = useState({ balance: 0, unlimited: false, packs: [] });
  const [paygate, setPaygate] = useState({ paypal_enabled: false, stripe_enabled: false, payfast_enabled: false });
  const [loading, setLoading] = useState(true);
  const [selectedPack, setSelectedPack] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [packs, pg] = await Promise.all([
        getResellerCreditPacks(),
        fetch(`${BACKEND}/api/public/paygate-status`).then((r) => (r.ok ? r.json() : null)),
      ]);
      setState(packs);
      if (pg) setPaygate(pg);
    } catch (e) {
      toast({
        title: "Failed to load",
        description: e?.response?.data?.detail || e?.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const gatewayUrl = (gateway, pack, orderId) => {
    if (gateway === "paypal" && paygate.paypal_business_email) {
      const params = new URLSearchParams({
        cmd: "_xclick",
        business: paygate.paypal_business_email,
        item_name: `ZA Player Pro — ${pack.name} (order ${orderId})`,
        amount: pack.price_display.replace(/[^0-9.]/g, ""),
        currency_code: paygate.paypal_currency || "USD",
        custom: orderId,
      });
      return `https://www.paypal.com/cgi-bin/webscr?${params}`;
    }
    if (gateway === "payfast" && paygate.payfast_merchant_id) {
      const host =
        paygate.payfast_environment === "live"
          ? "https://www.payfast.co.za"
          : "https://sandbox.payfast.co.za";
      const params = new URLSearchParams({
        merchant_id: paygate.payfast_merchant_id,
        merchant_key: paygate.payfast_merchant_key || "",
        amount: pack.price_display.replace(/[^0-9.]/g, ""),
        item_name: `ZA Player Pro — ${pack.name}`,
        m_payment_id: orderId,
        custom_str1: orderId,
      });
      return `${host}/eng/process?${params}`;
    }
    // Stripe placeholder — same order id tracked
    return `mailto:support@zaplayerpro.club?subject=${encodeURIComponent(
      `Buy ${pack.name} · order ${orderId}`,
    )}`;
  };

  const startPurchase = async (gateway) => {
    if (!selectedPack) return;
    setBusy(true);
    try {
      const intent = await createResellerPurchaseIntent(selectedPack.id, gateway);
      if (gateway === "payfast") {
        // Server builds signed, FX-converted form and we auto-submit.
        const amount = parseFloat(String(selectedPack.price_display).replace(/[^0-9.]/g, "")) || 0;
        const res = await fetch(`${BACKEND}/api/paygate/payfast/build`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            purpose: "credits",
            amount,
            source_currency: "USD",
            item_name: `ZA Player Pro — ${selectedPack.name}`,
            order_id: intent.order_id,
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          toast({
            title: "PayFast checkout failed",
            description: data?.detail || "Try again in a moment.",
            variant: "destructive",
          });
          return;
        }
        const form = document.createElement("form");
        form.method = "POST";
        form.action = data.action_url;
        form.style.display = "none";
        Object.entries(data.fields).forEach(([k, v]) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = k;
          input.value = v == null ? "" : String(v);
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
        return;
      }
      const url = gatewayUrl(gateway, selectedPack, intent.order_id);
      window.open(url, "_blank", "noopener,noreferrer");
      toast({
        title: "Order pending",
        description: `Order ${intent.order_id.slice(0, 8)} recorded. Your credits will be added once payment is confirmed.`,
      });
      setSelectedPack(null);
    } catch (e) {
      toast({
        title: "Purchase failed",
        description: e?.response?.data?.detail || e?.message,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const anyGateway = paygate.paypal_enabled || paygate.stripe_enabled || paygate.payfast_enabled;

  if (loading) {
    return <div className="p-6 text-neutral-500" data-testid="buy-credits-loading">Loading…</div>;
  }

  return (
    <div className="space-y-6" data-testid="reseller-buy-credits-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-3 text-2xl font-bold">
            <Wallet className="h-7 w-7 text-emerald-400" />
            Buy credits
          </h1>
          <p className="text-sm text-neutral-400">
            Top up your credit balance to generate more activation keys.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          data-testid="buy-credits-refresh"
        >
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      {/* Balance */}
      <Card className="border-white/10 bg-neutral-900">
        <CardContent className="flex flex-wrap items-center justify-between gap-4 p-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500">
              Current balance
            </div>
            <div className="mt-1 flex items-center gap-2 text-3xl font-extrabold text-white" data-testid="balance-display">
              {state.unlimited ? (
                <>
                  <InfinityIcon className="h-7 w-7 text-emerald-400" />
                  Unlimited
                </>
              ) : (
                <>{state.balance} credits</>
              )}
            </div>
          </div>
          {state.unlimited && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm text-emerald-100">
              You have unlimited credits — buying packs is optional.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Packs grid */}
      {state.packs.length === 0 ? (
        <Card className="border-white/10 bg-neutral-900">
          <CardContent className="p-6 text-sm text-neutral-500">
            No credit packs are configured yet. Please contact your administrator.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-3" data-testid="pack-grid">
          {state.packs.map((p) => (
            <Card
              key={p.id}
              className="border-white/10 bg-neutral-900"
              data-testid={`pack-${p.id}`}
            >
              <CardHeader>
                <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400">
                  <Package className="h-5 w-5" />
                </div>
                <CardTitle className="text-base">{p.name}</CardTitle>
                <CardDescription className="text-xs text-neutral-500">
                  {p.credits} activation credits
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-3xl font-extrabold text-white">
                  {p.price_display}
                </div>
                {p.price_hint && (
                  <div className="mt-1 text-[11px] uppercase tracking-widest text-neutral-500">
                    {p.price_hint}
                  </div>
                )}
                <Button
                  onClick={() => setSelectedPack(p)}
                  disabled={!anyGateway}
                  className="mt-4 w-full rounded-full bg-emerald-500 text-black hover:bg-emerald-400 disabled:opacity-40"
                  data-testid={`pack-${p.id}-buy`}
                >
                  <CreditCard className="mr-2 h-4 w-4" /> Buy pack
                </Button>
                {!anyGateway && (
                  <p className="mt-2 text-[11px] text-neutral-500">
                    No payment gateway configured. Contact your admin.
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Gateway picker */}
      <Dialog open={!!selectedPack} onOpenChange={(o) => !o && setSelectedPack(null)}>
        <DialogContent className="border-white/10 bg-neutral-900 text-white" data-testid="reseller-gateway-picker">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-emerald-400" />
              Choose payment method
            </DialogTitle>
            <DialogDescription className="text-neutral-400">
              {selectedPack && (
                <>Buying <span className="text-emerald-400">{selectedPack.name}</span> for{" "}
                <span className="text-emerald-400">{selectedPack.price_display}</span>.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {paygate.paypal_enabled && (
              <Button
                onClick={() => startPurchase("paypal")}
                disabled={busy}
                className="flex w-full items-center justify-between rounded-xl bg-[#003087] py-6 text-base font-semibold text-white hover:bg-[#001F5C]"
                data-testid="reseller-pay-paypal"
              >
                <span className="flex items-center gap-3">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded bg-white text-sm font-bold text-[#003087]">PP</span>
                  Pay with PayPal
                </span>
              </Button>
            )}
            {paygate.payfast_enabled && (
              <Button
                onClick={() => startPurchase("payfast")}
                disabled={busy}
                className="flex w-full items-center justify-between rounded-xl bg-[#ED1C24] py-6 text-base font-semibold text-white hover:bg-[#C41820]"
                data-testid="reseller-pay-payfast"
              >
                <span className="flex items-center gap-3">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded bg-white text-sm font-bold text-[#ED1C24]">PF</span>
                  Pay with PayFast
                </span>
                <span className="text-xs opacity-80">
                  {paygate.payfast_environment === "live" ? "Live" : "Sandbox"}
                </span>
              </Button>
            )}
            {paygate.stripe_enabled && (
              <Button
                onClick={() => startPurchase("stripe")}
                disabled={busy}
                className="flex w-full items-center justify-between rounded-xl bg-[#635BFF] py-6 text-base font-semibold text-white hover:bg-[#4B45CC]"
                data-testid="reseller-pay-stripe"
              >
                <span className="flex items-center gap-3">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded bg-white text-sm font-bold text-[#635BFF]">S</span>
                  Pay with card (Stripe)
                </span>
              </Button>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSelectedPack(null)}
              disabled={busy}
              className="border-white/20 bg-white/5 text-white hover:bg-white/10"
              data-testid="reseller-gateway-cancel"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {busy ? "Recording order…" : "Cancel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
